import { z } from "zod";
import {
  IssueCategory,
  IssueSubcategory,
  procedureSchema,
  procedureSessionEnvelopeSchema,
  sessionDocumentWithSampleNumbersSchema,
} from "./auditSession";
import { lifecycleStateSchema, testsStateSchema } from "./lifecycleState";
import {
  ControlExceptionType,
  PerformanceIssueType,
  OutcomeType,
} from "./attributeTestColumn";
import {
  analyticsControlException,
  analyticsReviewerType,
} from "./analyticsEnums";
import { judgeProvenanceOf } from "./analyticsColumnResult";

/**
 * Slim per-attribute-test-result projection that ships on the dry source-row
 * envelope so the workpaper table's `AttrTestColumnCell` can render its
 * `Issues Found` / `No Issues Found` badge without firing the heavy
 * `useSourceRow` per visible row.
 *
 * Mirrors Friday cross-repo contract A: the BE projects these fields onto
 * both `/source-rows` and `/table-shell`. The heavy text fields
 * (`reperformanceSteps`, `inlineCitations`, `evidenceSummary`,
 * `outcomeDescription`, `potentialMitigatingFactors`, `partialDescription`)
 * are deliberately omitted — the side panel lazy-fetches them via
 * `useAttributeTestResultDetail` on click.
 */
export const attributeTestResultSummarySchema = z.object({
  id: z.string(),
  attributeTestColumnId: z.string(),
  outcome: OutcomeType.nullable().optional(),
  isConclusive: z.boolean(),
  controlExceptions: z.array(ControlExceptionType).default([]),
  performanceIssues: z.array(PerformanceIssueType).default([]),
  reviewOrder: z.number().default(0),
  columnVersion: z.number().default(0),
  runId: z.string().nullable().optional(),
  reviewerType: z.enum(["human", "system"]).default("system"),
  userId: z.string().nullable().optional(),
  lockedBy: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type AttributeTestResultSummary = z.infer<
  typeof attributeTestResultSummarySchema
>;

/**
 * Slim per-analytics-result projection the grid renders an analytics cell from,
 * so a badge costs no per-row fetch.
 *
 * Modeled on `attributeTestResultSummarySchema`, so an analytics cell reads the
 * same fields as an attribute-test one, with deliberate divergences: no
 * `reviewOrder` (only the attribute-test side resolves a legacy `checkResultId`
 * through it), and per-field fallbacks on `outcome` and `reviewerType` where the
 * sibling has none.
 *
 * Those fallbacks are where the two shapes are meant to differ. The sibling's
 * strictness on them is an unfixed gap rather than a considered design — it
 * drops a whole verdict over an enum member the backend grew — and matching it
 * would be inheriting a latent defect, not staying consistent with a decision.
 * Repairing the sibling is a separate change; it is not made here, so the two
 * summaries do diverge in the meantime, and a reader comparing them will find
 * this the only place the reason is written down.
 *
 * It carries both run ids because they answer different questions. `runId` is the
 * Midship `session_run` that produced the verdict, and it is what clears a failed
 * run's error badge off a cell that run nevertheless answered. `runManifestId`
 * names an AuditBoard workflow run and cannot stand in for it: reading one as the
 * other suppresses a badge against the wrong run.
 *
 * What is omitted for weight: the prose the review pane lazy-fetches with the
 * full result — `outcomeDescription`, `evidenceSummary`, `partialDescription`,
 * `potentialMitigatingFactors` — plus `criteriaFingerprint`.
 *
 * What this projection does not model: `performanceIssues`. The badge is
 * derived from `outcome`, `isConclusive` and the exception list, and reads no
 * reason for an inconclusive verdict, so carrying the list here would add weight
 * to every row for a field no cell consults. The review pane reads it off the
 * full verdict, which declares it, and lists the reasons beside the prose that
 * explains them.
 *
 * `controlExceptions` carries no default. The badge no longer reads it — the
 * verdict alone decides the status, and a pass badges as a pass whatever its
 * exceptions (MS-3733) — but it rides onto the cell's issue list, and a default
 * would absorb a projection that stopped sending the field into a confident `[]`.
 * Required, a row that omits it fails element parse and is dropped (see
 * `analyticsResultsSummary`), so the cell renders no verdict rather than one
 * claiming no exceptions it was never told about.
 *
 * An exception this build cannot *name* is a different question from a missing
 * field: `analyticsControlException` coarsens it to the catch-all rather than
 * dropping the row. No badge depends on which members the list holds, so keeping
 * an exception the taxonomy has outgrown preserves the status exactly; dropping
 * the row would take the verdict off its cell, and the rows that vanished would
 * be disproportionately the exception-bearing ones. The cost is that a coarsened
 * member is indistinguishable from a verdict that genuinely reported the
 * catch-all. A member that is not a string is not coarsened — it fails, and the
 * row drops.
 *
 * It carries `lockedBy` and `userId` deliberately: the badge reads `lockedBy` to
 * show who owns the cell, and `userId` attributes a human override's author.
 * Without them an analytics cell would look unattributed beside an
 * attribute-test one.
 *
 * Judge provenance — model, effort and prompt version — is carried because
 * `verdictMethodOf` takes the whole group, so the summary answers through the
 * same reconstruction as the full verdict. On the grid the method decides only
 * whether the line names Midship or an auditor, which `reviewerType` settles on
 * its own; telling a judgment from a refusal is the review pane's job, and the
 * pane reads the full verdict. `criteriaFingerprint` is the one member of the
 * group left off: the projection does not send it, and the group is
 * all-or-nothing on the verdict, so the three that are here answer the same
 * question all four would. Each falls back to null rather than failing, for the
 * reason `outcome` does — a wire that sends a non-string should cost the judge
 * group, not the row.
 *
 * What is omitted because declaring it would be worse than deriving it:
 * `verdictMethod`. The wire sends it, and this schema deliberately does not read
 * it. A closed vocabulary at a parse boundary can be handed a member this build
 * has not heard of, and the only answers then are to drop the verdict — reporting
 * a tested cell as untested — or to resolve it to whichever member it most
 * resembles, which attributes a machine's mark to an auditor or the reverse.
 * `verdictMethodOf` reconstructs it instead, from the reviewer and the judge
 * group above, and answers for this summary and for the full verdict through the
 * one function.
 *
 * The all-or-nothing judge rule is refined on the full result, not here: a
 * summary is a badge's input rather than the record that rule protects, and this
 * projection declares part of the group by design, which is the shape the
 * refinement exists to reject. The three it does declare are resolved as one
 * group instead, which holds them to the rule without refusing the row.
 */
const analyticsResultSummaryShape = z.object({
  id: z.string(),
  analyticsColumnId: z.string(),

  /**
   * Falls back to null rather than failing, so a verdict whose outcome this
   * build cannot read still reaches its own cell instead of being dropped and
   * reading as a sample that was never tested. The fallback is fail-closed and
   * cannot flatter the wire: the badge translator maps a conclusive verdict
   * carrying no readable outcome to a failure, never to a pass.
   *
   * The cost is that an outcome the taxonomy grows into badges red rather than
   * as whatever gentler thing it names. That is conservative, and it is far
   * better than the verdict vanishing off the grid.
   */
  outcome: OutcomeType.nullable().optional().catch(null),
  isConclusive: z.boolean(),
  controlExceptions: z.array(analyticsControlException),
  populationTotal: z.number().nullable().optional(),
  runId: z.string().nullable().optional(),
  runManifestId: z.string().nullable().optional(),
  columnVersion: z.number().default(0),

  /**
   * Falls back to the agent rather than failing, for the same reason `outcome`
   * does: a writer category this build cannot name should cost the attribution
   * line, not the verdict. `.catch` subsumes the default it replaces — a default
   * fires only on `undefined`, so it never rescued an unrecognised value, which
   * is the case that drops a row.
   *
   * The residual is real and is not closed here: if a third writer category ever
   * ships and it denotes a kind of human, two shared surfaces read the fallback
   * rather than the wire. The result card attributes those verdicts to the
   * agent, and the grid's staleness check exempts only a `human` verdict, so an
   * unlocked one whose column has since moved also takes a ⚠ an auditor's own
   * conclusion would not. Both are read by the eval and attribute-test cells
   * too, so rewording them is not this schema's to do.
   *
   * Deriving the fallback from whether `userId` is set would close the common
   * case, but it is not airtight and is deliberately not used: the backend's
   * `user_id` is `ON DELETE SET NULL`, so an override can legally outlive the
   * auditor who made it and present as a human verdict with no user.
   */
  reviewerType: analyticsReviewerType.catch("system"),

  /**
   * Judge provenance, less the fingerprint the projection does not send. The
   * wire sends the three together or not at all, because the verdict's columns
   * move as a block; nothing on these fields holds them to that. The transform
   * below does instead, resolving them as one `JudgeProvenance`, so a group that
   * arrives part-set reads as no judge rather than as half of one.
   */
  judgeModelId: z.string().nullable().optional().catch(null),
  judgeEffort: z.string().nullable().optional().catch(null),
  judgePromptVersion: z.string().nullable().optional().catch(null),

  userId: z.string().nullable().optional(),
  lockedBy: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const analyticsResultSummarySchema =
  analyticsResultSummaryShape.transform((summary) => ({
    ...summary,
    ...judgeProvenanceOf(summary),
  }));

export type AnalyticsResultSummary = z.infer<
  typeof analyticsResultSummarySchema
>;

/**
 * Slim per-check-result projection that ships on the dry source-row envelope
 * so the workpaper table's `EvalColumnCell` can render its badge without
 * firing the heavy `useSourceRow` per visible row.
 *
 * Mirrors Friday cross-repo contract A: heavy fields (`reason`, `citations`,
 * `old_citations`) are omitted — `CheckAnalysisV2` (the eval-column side
 * panel) continues to read those from `useSourceRow` on click.
 */
export const checkResultSummarySchema = z.object({
  id: z.string(),
  evalColumnId: z.string().nullable(),
  checkId: z.string().nullable(),
  decision: z.enum(["pass", "fail", "not_applicable"]),
  issues: z
    .array(
      z.object({
        issueCategory: IssueCategory,
        issueSubcategory: IssueSubcategory,
        description: z.string(),
      }),
    )
    .default([]),
  reviewOrder: z.number().default(0),
  columnVersion: z.number().default(0),
  runId: z.string().nullable().optional(),
  reviewerType: z.enum(["human", "system"]).default("system"),
  userId: z.string().nullable().optional(),
  lockedBy: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type CheckResultSummary = z.infer<typeof checkResultSummarySchema>;

/**
 * The cell a verdict belongs to, read off an element the summary schema refused.
 *
 * Narrow on purpose: it asks only for the field that attributes an element to a
 * cell, so it answers for a verdict that is malformed in any other way. A verdict
 * damaged badly enough to lose this field cannot be attributed to a cell by any
 * design, and is dropped unattributed.
 */
const analyticsVerdictCellSchema = z.object({
  analyticsColumnId: z.string(),
});

/**
 * One element of the grid's analytics summary list, resolved to either the
 * verdict or the cell whose verdict was dropped.
 *
 * The element never fails, so one unreadable verdict cannot take the row — and
 * with it every other column's badge on that sample — down with it. What it
 * yields is split apart on the row below.
 */
const analyticsResultSummaryElementSchema = z.unknown().transform((element) => {
  const verdict = analyticsResultSummarySchema.safeParse(element);
  if (verdict.success) {
    return { verdict: verdict.data, droppedForColumn: null };
  }

  const cell = analyticsVerdictCellSchema.safeParse(element);
  return {
    verdict: null,
    droppedForColumn: cell.success ? cell.data.analyticsColumnId : null,
  };
});

/**
 * Phase 3 table-shell wire shape for
 * `GET /controls/v2/procedure-session/{ps_id}/table-shell`.
 *
 * Dry (light) source-row entry — no nested `check_results`,
 * `attribute_test_results`, `match_items`, etc. Per-row hydration of those
 * heavy lists happens lazily via `useSourceRow(psId, srId)` on selection.
 *
 * Backend serialises via Pydantic `alias_generator=to_camel`, so this matches
 * the BE JSON exactly. The two `*State` fields (`matchItemsState`,
 * `testsState`) mirror the BE model `DrySessionSourceRow` at
 * `midship/models/heretic/session_source_row.py`.
 *
 * Workpaper-lazy-load augmentation: `attributeTestResultsSummary` and
 * `checkResultsSummary` carry the badge-rendering fields for the attribute-test
 * and eval columns respectively. Default to `[]` so legacy responses
 * (`/table-shell` requests that pre-date the BE change) still parse cleanly.
 */
const drySessionSourceRowShape = z.object({
  id: z.string(),
  data: z.record(z.unknown()),
  matchItemsState: lifecycleStateSchema,
  testsState: testsStateSchema,
  sampleNumber: z.number().nullable(),
  // Emitted by the slim list endpoint `/source-rows` only; the legacy
  // `/table-shell` endpoint does not project this column, so it stays optional
  // on the shared dry-row schema.
  documentCount: z.number().optional(),
  attributeTestResultsSummary: z
    .array(attributeTestResultSummarySchema)
    .default([]),
  // A malformed element — e.g. a required field the projection stopped sending
  // after a backend-first deploy — drops that single verdict rather than failing
  // the whole table-shell parse and stranding every other row's badge. A dropped
  // verdict leaves its cell holding none, the fail-closed state, so it never
  // renders as a clean pass.
  analyticsResultsSummary: z
    .array(analyticsResultSummaryElementSchema)
    .default([]),
  checkResultsSummary: z.array(checkResultSummarySchema).default([]),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/**
 * The parsed row, with a dropped analytics verdict kept visible.
 *
 * `analyticsResultsSummary` holds the verdicts that parsed and nothing else, so
 * every reader of it — the live-verdict lookup, the lock check, the cell
 * renderers — is unchanged and needs no narrowing. The cells whose verdict was
 * discarded travel beside it instead, because a cell that finds no verdict
 * renders as a bare box with no click handler, pixel-identical to a sample that
 * was never tested; naming those cells is what lets the grid say the verdict was
 * unreadable rather than absent.
 *
 * `analyticsVerdictsDroppedForColumns` is a parse artifact and not a field the
 * backend sends. It is named for analytics because only analytics can populate
 * it: the attribute-test summary list has no element tolerance, so a malformed
 * attribute-test verdict fails the row outright rather than dropping, and a
 * generic name would read as covering all three column kinds while being
 * permanently empty for two.
 *
 * The split lives on the schema rather than in a hook because two envelopes wrap
 * this row — the table shell and the paginated source-row page — and a
 * hook-level derivation would have to be written once for each.
 *
 * It does not cover a verdict too damaged to name its column: the list holds the
 * cells that can be identified, and an element that lost `analyticsColumnId` is
 * dropped with no marker, exactly as before.
 */
export const drySessionSourceRowSchema = drySessionSourceRowShape.transform(
  (row) => {
    const elements = row.analyticsResultsSummary;
    return {
      // Spread rather than rebuilt field by field: this row also carries the
      // attribute-test and eval summaries, and picking fields here would make
      // every later field added to the shape silently absent downstream.
      ...row,
      analyticsResultsSummary: elements.flatMap((element) =>
        element.verdict === null ? [] : [element.verdict],
      ),
      analyticsVerdictsDroppedForColumns: elements.flatMap((element) =>
        element.droppedForColumn === null ? [] : [element.droppedForColumn],
      ),
    };
  },
);

export type DrySessionSourceRow = z.infer<typeof drySessionSourceRowSchema>;

export const procedureSessionTableShellSchema = z.object({
  procedureSession: procedureSessionEnvelopeSchema,
  procedure: procedureSchema,
  documents: z.array(sessionDocumentWithSampleNumbersSchema).default([]),
  sourceRows: z.array(drySessionSourceRowSchema).default([]),
});

export type ProcedureSessionTableShell = z.infer<
  typeof procedureSessionTableShellSchema
>;

/**
 * Paginated wire shape for the slim list endpoint:
 * `GET /controls/v2/procedure-session/{ps_id}/source-rows`.
 *
 * Defined here (not in `auditSession.ts`) to avoid a circular import:
 * `drySessionSourceRowSchema` lives in this file and depends on
 * `auditSession.ts`, so the page schema is co-located with its row schema.
 */
export const sourceRowsPageSchema = z.object({
  sourceRows: z.array(drySessionSourceRowSchema),
  page: z.number(),
  pageSize: z.number(),
  total: z.number(),
});

export type SourceRowsPage = z.infer<typeof sourceRowsPageSchema>;
